import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  getConsent,
  saveConsent,
  DEFAULT_CONSENT,
} from "@/lib/cookieConsent";

function PreferenceRow({ title, description, checked, disabled, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 p-4">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        data-testid={testid}
        aria-label={title}
      />
    </div>
  );
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      setPrefs({ ...DEFAULT_CONSENT, ...existing.categories, essential: true });
    } else {
      setVisible(true);
    }
    const onReset = (e) => {
      if (!e.detail) {
        setPrefs(DEFAULT_CONSENT);
        setVisible(true);
      }
    };
    const onOpenPrefs = () => {
      const c = getConsent();
      setPrefs(c ? { ...DEFAULT_CONSENT, ...c.categories, essential: true } : DEFAULT_CONSENT);
      setPrefsOpen(true);
    };
    window.addEventListener("gx-cookie-consent-updated", onReset);
    window.addEventListener("gx-open-cookie-preferences", onOpenPrefs);
    return () => {
      window.removeEventListener("gx-cookie-consent-updated", onReset);
      window.removeEventListener("gx-open-cookie-preferences", onOpenPrefs);
    };
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setVisible(false);
  };

  const savePreferences = () => {
    saveConsent(prefs);
    setPrefsOpen(false);
    setVisible(false);
  };

  if (!visible && !prefsOpen) return null;

  return (
    <>
      {visible && (
        <div
          className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
          data-testid="cookie-consent-banner"
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-4 sm:px-8 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:flex">
                <Cookie className="h-5 w-5" />
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                We use cookies to improve your experience and analyze usage. By continuing, you agree to our{" "}
                <Link
                  to="/cookie-policy"
                  className="font-semibold text-primary underline-offset-2 hover:underline"
                  data-testid="cookie-policy-link"
                >
                  Cookie Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setPrefsOpen(true)}
                data-testid="cookie-manage-preferences-btn"
              >
                Manage preferences
              </Button>
              <Button onClick={acceptAll} data-testid="cookie-accept-btn">
                Accept
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="cookie-preferences-modal">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which cookies you allow. Essential cookies are always active.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <PreferenceRow
              title="Essential"
              description="Required for the Service to function — session, authentication, and security. These cannot be disabled."
              checked
              disabled
              onChange={() => {}}
              testid="cookie-toggle-essential"
            />
            <PreferenceRow
              title="Analytics"
              description="Help us understand how GovernX is used so we can improve the product."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              testid="cookie-toggle-analytics"
            />
            <PreferenceRow
              title="Marketing"
              description="Measure the effectiveness of our marketing, including cross-product promotion."
              checked={prefs.marketing}
              onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              testid="cookie-toggle-marketing"
            />
          </div>
          <DialogFooter>
            <Button onClick={savePreferences} data-testid="cookie-save-preferences-btn">
              Save preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
